import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { pdApi } from '../api';
import '../screens.css';
import './Optimizer.css';
import { HelpPanel } from '../HelpPanel';
import { OPT_ANALYTICS, OPT_SAVED_SCENARIOS } from '../helpContent';

/**
 * Portfolio Optimizer → Saved Scenarios and Analytics & Visuals.
 *
 * Cloned from `views/optimizer/saved-scenarios.ejs` and `views/optimizer/analytics-visuals.ejs`.
 *
 * A saved scenario is a SNAPSHOT, not a live view: the cost, benefit and per-criterion
 * scores are copied onto the scenario's rows at save time, so reopening one shows what was
 * decided then rather than what the numbers say now. That is the point of saving it.
 *
 * The stored weights are FRACTIONS summing to 1, not the percentages the builder shows —
 * the server normalises on save. They are rendered back as percentages here.
 */

type Dict = Record<string, unknown>;

const money = (v: unknown) =>
  v === null || v === undefined
    ? '—'
    : Number(v).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
      });

export function SavedScenarios() {
  const [scenarios, setScenarios] = useState<Dict[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [open, setOpen] = useState<Dict | null>(null);
  /*
   * COMPARE — restored. The source screen's whole purpose is weighing runs against each
   * other (`saved-scenarios.ejs` header comment: "Added Compare functionality and
   * checkboxes"), and the port shipped with only Open and Delete. Selection lives here and
   * travels to the comparison as ids in the query string, exactly as the source does, so a
   * comparison is a link someone can send.
   */
  const [ticked, setTicked] = useState<number[]>([]);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await pdApi.optimizer.scenarios();
      setScenarios(data.scenarios);
      const live = new Set(data.scenarios.map((s) => Number(s.scenario_id)));
      setTicked((prev) => prev.filter((id) => live.has(id)));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const openOne = async (id: number) => {
    setError(null);
    try {
      const r = await pdApi.optimizer.scenario(id);
      setOpen(r.scenario);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <div className="pd-screen">
      <div className="pd-page-header">
        <div>
          <h1 className="pd-page-header__title">Saved Scenarios</h1>
          <p className="pd-page-header__subtitle">
            Each scenario stores the selection and the numbers as they stood when it was
            saved.
          </p>
        </div>
        <div className="pd-page-header__actions">
          <span className="pd-count">{scenarios.length} scenarios</span>
          <button
            type="button"
            className="pd-btn pd-btn--primary"
            disabled={ticked.length < 2}
            title={
              ticked.length < 2
                ? 'Tick two or more scenarios to compare them'
                : `Compare the ${ticked.length} selected scenarios`
            }
            onClick={() => navigate(`compare?ids=${ticked.join(',')}`)}
          >
            {ticked.length >= 2 ? `Compare Scenarios (${ticked.length})` : 'Compare Scenarios'}
          </button>
          <button
            type="button"
            className="pd-btn"
            title="Delete scenarios older than 90 days"
            onClick={async () => {
              if (!window.confirm('Delete every scenario older than 90 days?')) return;
              const r = await pdApi.optimizer.cleanup();
              setNotice(r.message);
              await load();
            }}
          >
            Clean up old
          </button>
        </div>
        <div className="pd-page-header__actions">
          <HelpPanel help={OPT_SAVED_SCENARIOS} />
        </div>
      </div>

      {error ? <div className="pd-error">{error}</div> : null}
      {notice ? <div className="pd-notice">{notice}</div> : null}

      {loading ? (
        <div className="pd-loading">Loading…</div>
      ) : (
        <div className="pd-table-wrap">
          <table className="pd-table">
            <thead>
              <tr>
                <th style={{ width: 40, textAlign: 'center' }}>
                  <input
                    type="checkbox"
                    aria-label="Select all scenarios"
                    title="Select all"
                    checked={scenarios.length > 0 && ticked.length === scenarios.length}
                    ref={(el) => {
                      if (el) el.indeterminate = ticked.length > 0 && ticked.length < scenarios.length;
                    }}
                    onChange={(e) =>
                      setTicked(e.target.checked ? scenarios.map((s) => Number(s.scenario_id)) : [])
                    }
                  />
                </th>
                <th style={{ width: 220 }}>Scenario</th>
                <th style={{ width: 150 }}>Project</th>
                <th style={{ width: 120 }}>Budget</th>
                <th style={{ width: 120 }}>Total Cost</th>
                <th style={{ width: 120 }}>Total Benefit</th>
                <th style={{ width: 90 }}>ROI</th>
                <th style={{ width: 80 }}>Picked</th>
                <th style={{ width: 150 }}>Created By</th>
                <th style={{ width: 130 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {scenarios.length === 0 ? (
                <tr>
                  <td colSpan={10} className="pd-empty">
                    No scenarios saved yet.
                  </td>
                </tr>
              ) : (
                scenarios.map((s) => (
                  <tr key={String(s.scenario_id)}>
                    <td style={{ textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        aria-label={`Select ${String(s.scenario_name)}`}
                        checked={ticked.includes(Number(s.scenario_id))}
                        onChange={(e) =>
                          setTicked((prev) =>
                            e.target.checked
                              ? [...prev, Number(s.scenario_id)]
                              : prev.filter((id) => id !== Number(s.scenario_id)),
                          )
                        }
                      />
                    </td>
                    <td>
                      <div>{String(s.scenario_name)}</div>
                      {s.scenario_description ? (
                        <div className="opt-desc">{String(s.scenario_description)}</div>
                      ) : null}
                    </td>
                    <td>{String(s.project_id)}</td>
                    <td className="pd-num">{money(s.budget_constraint)}</td>
                    <td className="pd-num">{money(s.total_cost_selected)}</td>
                    <td className="pd-num">{money(s.total_benefit_selected)}</td>
                    <td className="pd-num">
                      {s.portfolio_roi === null || s.portfolio_roi === undefined
                        ? '—'
                        : `${(Number(s.portfolio_roi) * 100).toFixed(1)}%`}
                    </td>
                    <td className="pd-num">{String(s.count_selected ?? 0)}</td>
                    <td>{String(s.created_by_name ?? '—')}</td>
                    <td>
                      <div className="pd-row-actions">
                        <button
                          type="button"
                          className="pd-icon-btn"
                          title="View"
                          onClick={() => openOne(Number(s.scenario_id))}
                        >
                          👁️
                        </button>
                        <button
                          type="button"
                          className="pd-icon-btn"
                          title="Delete"
                          onClick={async () => {
                            if (!window.confirm(`Delete "${String(s.scenario_name)}"?`)) return;
                            await pdApi.optimizer.deleteScenario(Number(s.scenario_id));
                            await load();
                          }}
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {open ? (
        <div className="pd-modal-backdrop" onClick={() => setOpen(null)}>
          <div
            className="pd-modal"
            style={{ maxWidth: 900 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="pd-modal__head">
              <h2 className="pd-modal__title">{String(open.scenario_name)}</h2>
              <button type="button" className="pd-icon-btn" onClick={() => setOpen(null)}>
                ✕
              </button>
            </div>
            <div className="pd-modal__body">
              <div className="opt-weights-readout">
                {[
                  ['ROI', open.weight_roi],
                  ['Criticality', open.weight_criticality],
                  ['Alignment', open.weight_alignment],
                  ['Benefit/Cost', open.weight_benefit_cost],
                  ['Risk', open.weight_risk],
                ].map(([label, value]) => (
                  <span key={String(label)}>
                    {String(label)}{' '}
                    <strong>
                      {value === null || value === undefined
                        ? '—'
                        : `${(Number(value) * 100).toFixed(0)}%`}
                    </strong>
                  </span>
                ))}
              </div>

              <div className="pd-table-wrap">
                <table className="pd-table">
                  <thead>
                    <tr>
                      <th style={{ width: 160 }}>Opportunity</th>
                      <th>Description</th>
                      <th style={{ width: 110 }}>Status</th>
                      <th style={{ width: 110 }}>Cost</th>
                      <th style={{ width: 110 }}>Benefit</th>
                      <th style={{ width: 90 }}>Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {((open.opportunities as Dict[]) ?? []).map((o) => (
                      <tr key={String(o.opportunity_id)}>
                        <td>{String(o.opportunity_id)}</td>
                        <td>{String(o.description ?? '')}</td>
                        <td>{String(o.selection_status)}</td>
                        <td className="pd-num">{money(o.estimated_cost_snapshot)}</td>
                        <td className="pd-num">{money(o.estimated_benefit_snapshot)}</td>
                        <td className="pd-num">
                          {o.portfolio_score_snapshot === null ||
                          o.portfolio_score_snapshot === undefined
                            ? '—'
                            : Number(o.portfolio_score_snapshot).toFixed(3)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="pd-modal__foot">
              <button type="button" className="pd-btn" onClick={() => setOpen(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Analytics & Visuals — scenario comparison across the whole library.
 *
 * The source renders Chart.js bar and scatter plots. This shows the same measures as a
 * sortable comparison rather than pulling a charting library in for one screen; the
 * numbers are identical and the ranking is what the screen is read for. Noted in
 * docs/DEVIATIONS.md as a presentation change, not a data one.
 */
export function OptimizerAnalytics() {
  const [scenarios, setScenarios] = useState<Dict[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const data = await pdApi.optimizer.scenarios();
        setScenarios(data.scenarios);
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const maxBenefit = Math.max(1, ...scenarios.map((s) => Number(s.total_benefit_selected ?? 0)));

  return (
    <div className="pd-screen">
      <div className="pd-page-header">
        <div>
          <h1 className="pd-page-header__title">Analytics &amp; Visuals</h1>
          <p className="pd-page-header__subtitle">
            Every saved scenario side by side — cost, benefit, ROI and how many
            opportunities each one funds.
          </p>
        </div>
        <div className="pd-page-header__actions">
          <HelpPanel help={OPT_ANALYTICS} />
        </div>
      </div>

      {error ? <div className="pd-error">{error}</div> : null}

      {loading ? (
        <div className="pd-loading">Loading…</div>
      ) : scenarios.length === 0 ? (
        <div className="pd-empty">No scenarios to compare yet.</div>
      ) : (
        <div className="opt-analytics">
          {scenarios.map((s) => {
            const benefit = Number(s.total_benefit_selected ?? 0);
            const cost = Number(s.total_cost_selected ?? 0);
            return (
              <div key={String(s.scenario_id)} className="opt-analytics-row">
                <div className="opt-analytics-name">
                  <div>{String(s.scenario_name)}</div>
                  <div className="opt-desc">{String(s.project_id)}</div>
                </div>
                <div className="opt-analytics-bars">
                  <div className="opt-bar-track" title={`Benefit ${money(benefit)}`}>
                    <div
                      className="opt-bar opt-bar--benefit"
                      style={{ width: `${(benefit / maxBenefit) * 100}%` }}
                    />
                  </div>
                  <div className="opt-bar-track" title={`Cost ${money(cost)}`}>
                    <div
                      className="opt-bar opt-bar--cost"
                      style={{ width: `${(cost / maxBenefit) * 100}%` }}
                    />
                  </div>
                </div>
                <div className="opt-analytics-nums">
                  <span>{money(benefit)} benefit</span>
                  <span>{money(cost)} cost</span>
                  <span>
                    {s.portfolio_roi === null || s.portfolio_roi === undefined
                      ? '—'
                      : `${(Number(s.portfolio_roi) * 100).toFixed(1)}% ROI`}
                  </span>
                  <span>{String(s.count_selected ?? 0)} picked</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
